import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "../lib/errors";

export default function Register() {
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const payload = { email, password, name };
      if (referralCode.trim()) payload.referral_code = referralCode.trim().toUpperCase();
      await api.post("/auth/register", payload);
      await refresh();
      toast.success(
        referralCode
          ? "Welcome — enjoy 10 free days on the house"
          : "Welcome to the Playbook"
      );
      navigate("/app");
    } catch (err) {
      const msg = apiErrorMessage(err, "Registration failed");
      if (msg.toLowerCase().includes("already registered")) {
        toast.error("That email is already registered — try logging in instead");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain relative flex items-center justify-center px-6">
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10" data-testid="register-brand">
          <div className="w-8 h-8 border border-[#00D4FF] rotate-45 flex items-center justify-center">
            <span className="-rotate-45 text-[#00D4FF] font-mono text-xs font-bold">M</span>
          </div>
          <div>
            <div className="font-display text-lg leading-none">Martingale</div>
            <div className="overline leading-none mt-1">PLAYBOOK·TERMINAL</div>
          </div>
        </Link>

        <div className="overline mb-2">NEW·ACCOUNT</div>
        <h1 className="font-display text-4xl mb-8">Get the daily card.</h1>

        {referralCode && (
          <div
            className="mb-6 border border-[#00D4FF]/40 bg-[#00D4FF]/5 p-4 text-sm"
            data-testid="referral-banner"
          >
            <div className="overline neon-text mb-1">REFERRAL · UNLOCKED</div>
            <div className="text-zinc-300">
              Code <span className="mono text-[#00D4FF]">{referralCode}</span> gives you
              10 free days instead of the usual 3.
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-5 border border-zinc-800 p-8 bg-zinc-950/40">
          <div>
            <label className="overline block mb-2">NAME (OPTIONAL)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="register-name"
              className="w-full bg-transparent border border-zinc-800 px-3 py-3 mono text-sm focus:outline-none focus:border-[#00D4FF]"
              placeholder="The Sharp"
            />
          </div>
          <div>
            <label className="overline block mb-2">EMAIL</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email"
              className="w-full bg-transparent border border-zinc-800 px-3 py-3 mono text-sm focus:outline-none focus:border-[#00D4FF]"
              placeholder="you@book.com"
            />
          </div>
          <div>
            <label className="overline block mb-2">PASSWORD</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password"
              className="w-full bg-transparent border border-zinc-800 px-3 py-3 mono text-sm focus:outline-none focus:border-[#00D4FF]"
              placeholder="at least 6 characters"
            />
          </div>
          <div>
            <label className="overline block mb-2">REFERRAL CODE (OPTIONAL)</label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              data-testid="register-referral"
              className="w-full bg-transparent border border-zinc-800 px-3 py-3 mono text-sm focus:outline-none focus:border-[#00D4FF]"
              placeholder="E.G. MP8F2A"
            />
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            By creating an account you confirm you are 21+ and agree to our{" "}
            <Link to="/terms" className="underline text-zinc-300">Terms</Link>,{" "}
            <Link to="/privacy" className="underline text-zinc-300">Privacy Policy</Link>{" "}
            and{" "}
            <Link to="/responsible-gambling" className="underline text-zinc-300">
              Responsible Gambling
            </Link>{" "}
            resources.
          </p>
          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit"
            className="w-full py-3 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs disabled:opacity-50 hover:brightness-110"
          >
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>

        <div className="mt-6 text-sm text-zinc-500">
          Already registered?{" "}
          <Link to="/login" className="text-[#00D4FF] underline" data-testid="register-to-login">
            Login
          </Link>
        </div>
      </div>
    </div>
  );
}